import { computed, ComputedRef, ref, Ref } from 'vue'

import { ReviewEntryDetail } from '@/domain/entities/review/reviewEntryDetail'
import { OpenReviewPostEntryViewModel } from '@/presentation/viewmodels/reviewPost/openReviewPostEntryViewModel'
import { PostReviewViewModel } from '@/presentation/viewmodels/reviewPost/postReviewViewModel'
import { ReviewPostEntryDetailViewModel } from '@/presentation/viewmodels/reviewPost/reviewPostEntryDetailViewModel'
import { ReviewPostPageStateViewModel } from '@/presentation/viewmodels/reviewPost/reviewPostPageStateViewModel'

export class ReviewPostPresenter {
  private reviewPostPageStateViewModel: ReviewPostPageStateViewModel
  private reviewPostEntryDetailViewModel: ReviewPostEntryDetailViewModel
  private openReviewPostEntryViewModel: OpenReviewPostEntryViewModel
  private postReviewViewModel: PostReviewViewModel
  private loading: Ref<boolean>

  constructor(
    reviewPostPageStateViewModel: ReviewPostPageStateViewModel,
    reviewPostEntryDetailViewModel: ReviewPostEntryDetailViewModel,
    openReviewPostEntryViewModel: OpenReviewPostEntryViewModel,
    postReviewViewModel: PostReviewViewModel
  ) {
    this.reviewPostPageStateViewModel = reviewPostPageStateViewModel
    this.reviewPostEntryDetailViewModel = reviewPostEntryDetailViewModel
    this.openReviewPostEntryViewModel = openReviewPostEntryViewModel
    this.postReviewViewModel = postReviewViewModel
    this.loading = ref(false)
  }

  get isLoading(): Ref<boolean> {
    return this.loading
  }

  async initState(problemId: string): Promise<void> {
    this.reviewPostPageStateViewModel.setEntrySummaryExpandedList({})
    this.reviewPostPageStateViewModel.setEntrySelectedReviewIndexList({})
    this.reviewPostPageStateViewModel.setEntryEditingList({})
    this.reviewPostEntryDetailViewModel.setReviewEntryDetailList({})
    this.postReviewViewModel.setReviewList({})
    await Promise.all([this.openReviewPostEntryViewModel.fetchReviewEntryList(problemId)])
  }

  async clickEntrySummary(entryId: string): Promise<void> {
    if (!this.reviewPostEntryDetailViewModel.isAlreadyDetailRequested(entryId)) {
      this.loading.value = true
      await this.reviewPostEntryDetailViewModel.fetchReviewEntryDetailList(entryId)
      this.loading.value = false
    }
    this.reverseEntrySummaryExpandedFlag(entryId)
  }

  getEntryDetail(entryId: string): ComputedRef<ReviewEntryDetail> {
    return computed(() => {
      const index = this.reviewPostPageStateViewModel.entrySelectedReviewIndexList.value[entryId]
      const detailList = this.reviewPostEntryDetailViewModel.reviewEntryDetailList.value[entryId]
      return detailList?.[index] ?? this.reviewPostEntryDetailViewModel.getEmptyEntryDetail()
    })
  }

  isAiReview(entryId: string): ComputedRef<boolean> {
    return computed(() => {
      const reviewerId = this.getEntryDetail(entryId).value.reviewerId
      return [
        'gpt-3.5-turbo',
        'gpt-3.5_turbo',
        'gemini-pro',
        'gemini_pro',
        'gpt_4o',
        'gemini_25_flash',
        'gpt_oss_120b'
      ].includes(reviewerId)
    })
  }

  private reverseEntrySummaryExpandedFlag(entryId: string): void {
    const currentFlag = this.reviewPostPageStateViewModel.entrySummaryExpandedList.value[entryId]
    this.reviewPostPageStateViewModel.setEntrySummaryExpandedFlag(entryId, !currentFlag)
  }
}
